import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Create a new context for impersonation handling
const ImpersonationContext = createContext({});

/**
 * ImpersonationProvider wraps the application and keeps track of the current
 * impersonation state (token + customer details). It also wires up a global
 * axios interceptor that automatically injects the impersonation JWT into
 * the Authorization header (Bearer scheme) for outgoing requests *unless* the
 * call already defines its own Authorization header.
 */
export const ImpersonationProvider = ({ children }) => {
  const [impersonationToken, setImpersonationToken] = useState(null);
  const [impersonatedCustomer, setImpersonatedCustomer] = useState(null);

  // Initialize impersonation state from localStorage on mount
  useEffect(() => {
    try {
      const savedState = localStorage.getItem('impersonationState');
      if (savedState) {
        const { token, customer } = JSON.parse(savedState);
        if (token && customer) {
          setImpersonationToken(token);
          setImpersonatedCustomer(customer);
        }
      }
    } catch (err) {
      console.warn('Failed to load impersonation state from localStorage', err);
      // Clear potentially corrupted data
      localStorage.removeItem('impersonationState');
    }
  }, []);

  // Attach / detach the axios request interceptor whenever the token changes
  useEffect(() => {
    const interceptorId = axios.interceptors.request.use(
      (config) => {
        // If we are impersonating *and* the request hasn't explicitly set its
        // own Authorization header, automatically append the Bearer token.
        if (impersonationToken && !config.headers?.Authorization) {
          config.headers = {
            ...config.headers,
            Authorization: `Bearer ${impersonationToken}`,
          };
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Cleanup interceptor when token changes / provider unmounts
    return () => {
      axios.interceptors.request.eject(interceptorId);
    };
  }, [impersonationToken]);

  const startImpersonation = ({ token, customer }) => {
    setImpersonationToken(token);
    setImpersonatedCustomer(customer);

    // Persist to localStorage
    try {
      localStorage.setItem('impersonationState', JSON.stringify({ token, customer }));
    } catch (err) {
      console.warn('Failed to save impersonation state to localStorage', err);
    }
  };

  const stopImpersonation = () => {
    setImpersonationToken(null);
    setImpersonatedCustomer(null);

    try {
      localStorage.removeItem('rechargeSession');
      localStorage.removeItem('shopifyCustomerAccessToken');
      localStorage.removeItem('impersonationState');
    } catch (err) {
      console.warn('Failed to clear auth tokens during impersonation exit', err);
    }
  };

  return (
    <ImpersonationContext.Provider
      value={{
        impersonationToken,
        impersonatedCustomer,
        isImpersonating: Boolean(impersonationToken),
        startImpersonation,
        stopImpersonation,
      }}
    >
      {children}
    </ImpersonationContext.Provider>
  );
};

// Hook to consume the impersonation context
export const useImpersonation = () => useContext(ImpersonationContext); 